import React, { useState, useCallback, useEffect } from 'react';
import { getENSDetails } from '../../../api/GBENSDec';
import dayjs from 'dayjs';

const useData = (state, setHeaderId, setCompanyId, setToRunInitialErrorCheck) => {
  const [data, setData] = useState({
    TransportAndJourney: {
      ENSRequestor: {
        country: '',
        EORI: '',
        companyName: '',
        streetAddress: '',
        townCity: '',
        postcode: '',
      },
      Route: {},
      Carrier: {},
      isEditable: 1,
    },
    Consignments: [],
    initialLoad: true,
  });

  const updateData = useCallback(
    (e, screen, field, modal, isEvent = true) => {
      //this fixes the text drag-and-drop issue we're having in the datetime picker
      if (!e || !screen) {
        return;
      }
      if (!screen) throw new Error('Screen is required');
      const value = e.$isDayjsObject || !isEvent ? e : e.target.value;
      if (!modal) {
        if (!data[screen]) setData((prev) => ({ ...prev, [screen]: { [field]: value } }));
        else setData((prev) => ({ ...prev, [screen]: { ...prev[screen], [field]: value } }));
      } else if (!data[screen]) {
        setData((prev) => ({ ...prev, [screen]: { [modal]: { [field]: value } } }));
      } else if (!data[screen][modal])
        setData((prev) => ({ ...prev, [screen]: { ...prev[screen], [modal]: { [field]: value } } }));
      else
        setData((prev) => ({
          ...prev,
          [screen]: { ...prev[screen], [modal]: { ...prev[screen][modal], [field]: value } },
        }));
    },
    [data]
  );

  const moveData = useCallback(
    (screen, modalFrom, modalTo, deleteModalFrom) => {
      if (!deleteModalFrom)
        setData((prev) => ({
          ...prev,
          [screen]: {
            ...prev[screen],
            [modalTo]: prev?.[screen]?.[modalFrom] ? JSON.parse(JSON.stringify(prev[screen][modalFrom])) : {},
          },
        }));
      else
        setData((prev) => ({
          ...prev,
          [screen]: {
            ...prev[screen],
            [modalTo]: prev?.[screen]?.[modalFrom] ? JSON.parse(JSON.stringify(prev[screen][modalFrom])) : {},
            [modalFrom]: undefined,
          },
        }));
    },
    [data]
  );

  const deleteData = useCallback(
    (screen, modal) => {
      setData((prev) => ({ ...prev, [screen]: { ...prev[screen], [modal]: undefined } }));
    },
    [data]
  );

  useEffect(() => {
    if (state?.companyId) {
      setCompanyId(state.companyId);
    }
  }, [state]);

  useEffect(() => {
    const fetch = async () => {
      setHeaderId(state.headerId);
      const dt = await getENSDetails(state.headerId);
      const consigments = dt.Consignments?.map((e) => {
        return {
          ...e,
          placeOfAcceptanceCountry: e.PlaceOfAcceptance?.country || '',
          placeOfAcceptancePlaceName: e.PlaceOfAcceptance?.placeName || '',
          placeOfDeliveryCountry: e.PlaceOfDelivery?.country || '',
          placeOfDeliveryPlaceName: e.PlaceOfDelivery?.placeName || '',
        };
      });
      dt.Consignments = consigments;

      if (dt.TransportAndJourney.arrivalDateTime)
        dt.TransportAndJourney.arrivalDateTime = dayjs(dt.TransportAndJourney.arrivalDateTime);
      if (dt.TransportAndJourney.departureDate)
        dt.TransportAndJourney.departureDateTime = dayjs(dt.TransportAndJourney.departureDate);

      // TODO: Temporary fix until we can change the Select & Text Input to pass in this format
      if (dt.TransportAndJourney.PlaceOfLoading.country) {
        dt.TransportAndJourney.placeOfLoadingCountry = dt.TransportAndJourney.PlaceOfLoading.country;
      }
      if (dt.TransportAndJourney.PlaceOfLoading.placeName) {
        dt.TransportAndJourney.placeOfLoadingName = dt.TransportAndJourney.PlaceOfLoading.placeName;
      }

      if (dt.TransportAndJourney.PlaceOfUnloading.country) {
        dt.TransportAndJourney.placeOfUnloadingCountry = dt.TransportAndJourney.PlaceOfUnloading.country;
      }
      if (dt.TransportAndJourney.PlaceOfUnloading.placeName) {
        dt.TransportAndJourney.placeOfUnloadingName = dt.TransportAndJourney.PlaceOfUnloading.placeName;
      }

      if (dt.TransportAndJourney.PlaceOfAcceptance.country) {
        dt.TransportAndJourney.placeOfAcceptanceCountry = dt.TransportAndJourney.PlaceOfAcceptance.country;
      }
      if (dt.TransportAndJourney.PlaceOfAcceptance.placeName) {
        dt.TransportAndJourney.placeOfAcceptanceName = dt.TransportAndJourney.PlaceOfAcceptance.placeName;
      }

      if (dt.TransportAndJourney.PlaceOfDelivery.country) {
        dt.TransportAndJourney.placeOfDeliveryCountry = dt.TransportAndJourney.PlaceOfDelivery.country;
      }
      if (dt.TransportAndJourney.PlaceOfDelivery.placeName) {
        dt.TransportAndJourney.placeOfDeliveryName = dt.TransportAndJourney.PlaceOfDelivery.placeName;
      }

      // TODO: temporary fix until we make the modals for these consistent
      if (dt.TransportAndJourney.Vehicle.Consignor) {
        dt.TransportAndJourney.Consignor = dt.TransportAndJourney.Vehicle.Consignor;
      }
      if (dt.TransportAndJourney.Vehicle.Consignee) {
        dt.TransportAndJourney.Consignee = dt.TransportAndJourney.Vehicle.Consignee;
      }

      if (dt.TransportAndJourney.portOfDepartureFreeText) {
        dt.TransportAndJourney.portOfDeparture = dt.TransportAndJourney.portOfDepartureFreeText;
      } else if (dt.TransportAndJourney.portOfDeparture === 0) {
        // backend sometimes returns 0 for null ports - for the user they should see nothing
        dt.TransportAndJourney.portOfDeparture = '';
      }

      if (!dt.Consignments || dt.Consignments === null) dt.Consignments = [];
      dt.Consignments = dt.Consignments.map((c) => {
        if (!c.lines) c.lines = [];
        if (!c.Consignee) c.Consignee = {};
        if (!c.Consignor) c.Consignor = {};
        // Add LRN fields from API response
        return {
          ...c,
          shipmentLRN: dt.TransportAndJourney?.shipmentLRN ?? '',
          consignmentLRN: c.consignmentLRN ?? '',
          previousLRN: dt.TransportAndJourney?.previousLRN ?? '',
        };
      });
      setData(dt);
      setToRunInitialErrorCheck(true);
    };
    if (state?.headerId) {
      fetch();
    }
  }, [state]);

  return { data, setData, updateData, moveData, deleteData };
};

export default useData;
